import {
  forwardRef,
  useId,
  type FocusEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { scrollFocusedToUpperThird } from '@client/lib/scroll-into-focus';

const INPUT_BASE =
  'w-full rounded-xl border border-slate-300 bg-white px-4 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:focus:border-slate-100 dark:focus:ring-slate-100/10';

type CommonFieldProps = {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  trailing?: ReactNode;
};

type FieldProps = CommonFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> & {
    inputClassName?: string;
  };

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, trailing, inputClassName = '', id, onFocus, ...rest },
  ref,
) {
  const reactId = useId();
  const inputId = id ?? reactId;
  const errorId = `${inputId}-error`;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const describedBy =
    [error ? errorId : null, hintId ?? null].filter(Boolean).join(' ') || undefined;
  // Wrap onFocus so callers can still observe focus events; we just tack on the
  // scroll behavior. Read-only / disabled inputs don't trigger soft keyboards
  // and don't need it, so guard.
  function handleFocus(e: FocusEvent<HTMLInputElement>): void {
    if (!e.target.readOnly && !e.target.disabled) {
      scrollFocusedToUpperThird(e.target);
    }
    onFocus?.(e);
  }
  return (
    <div className="flex flex-col gap-1.5 text-sm">
      <label htmlFor={inputId} className="font-medium">
        {label}
        {hint ? (
          <span id={hintId} className="ml-2 font-normal text-slate-400">
            — {hint}
          </span>
        ) : null}
      </label>
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          className={`h-12 ${INPUT_BASE} ${trailing ? 'pr-10' : ''} ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : ''} ${inputClassName}`.trim()}
          {...rest}
          onFocus={handleFocus}
        />
        {trailing ? (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
            {trailing}
          </span>
        ) : null}
      </div>
      {error ? (
        <p id={errorId} className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : null}
    </div>
  );
});

type TextareaFieldProps = CommonFieldProps &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> & {
    textareaClassName?: string;
  };

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(
  function TextareaField(
    { label, hint, error, textareaClassName = '', id, rows = 4, onFocus, ...rest },
    ref,
  ) {
    const reactId = useId();
    const inputId = id ?? reactId;
    const errorId = `${inputId}-error`;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const describedBy =
      [error ? errorId : null, hintId ?? null].filter(Boolean).join(' ') || undefined;
    function handleFocus(e: FocusEvent<HTMLTextAreaElement>): void {
      if (!e.target.readOnly && !e.target.disabled) {
        scrollFocusedToUpperThird(e.target);
      }
      onFocus?.(e);
    }
    return (
      <div className="flex flex-col gap-1.5 text-sm">
        <label htmlFor={inputId} className="font-medium">
          {label}
          {hint ? (
            <span id={hintId} className="ml-2 font-normal text-slate-400">
              — {hint}
            </span>
          ) : null}
        </label>
        <textarea
          ref={ref}
          id={inputId}
          rows={rows}
          aria-invalid={error ? true : undefined}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          className={`py-3 ${INPUT_BASE} ${error ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : ''} ${textareaClassName}`.trim()}
          {...rest}
          onFocus={handleFocus}
        />
        {error ? (
          <p id={errorId} className="text-sm text-red-600 dark:text-red-400">
            {error}
          </p>
        ) : null}
      </div>
    );
  },
);
